import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { 
  signInWithEmailAndPassword, 
  sendPasswordResetEmail,
} from 'firebase/auth';
import { sendPasswordResetEmail as sendEmailService } from '../utils/emailService';

const LoginForm = ({ 
  email, 
  setEmail, 
  password, 
  setPassword, 
  error, 
  isLoading, 
  onSubmit, 
  onForgotPassword, 
  navigate 
}) => (
  <div className="w-full">
    <h2 className="text-2xl font-bold text-center mb-6 text-white">Login</h2>
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-2 bg-white/10 border border-purple-400/50 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30"
          required
        />
      </div>
 
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Password
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-2 bg-white/10 border border-purple-400/50 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30"
          required
        />
      </div>

      {error && (
        <p className="text-red-500 text-sm text-center">{error}</p>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        {isLoading ? 'Signing in...' : 'Sign In'}
      </button>

      <div className="flex justify-between items-center mt-4">
        <button
          type="button"
          onClick={onForgotPassword}
          className="text-purple-400 hover:text-purple-300 text-sm"
        >
          Forgot password?
        </button>
        <Link
          to="/signup"
          className="text-purple-400 hover:text-purple-300 text-sm"
        >
          Create account
        </Link>
      </div>
    </form>
  </div>
);

const ForgotPasswordForm = ({ 
  email, 
  setEmail, 
  error, 
  successMessage, 
  isLoading, 
  onSubmit, 
  onBack 
}) => (
  <div className="w-full">
    <h2 className="text-2xl font-bold text-center mb-6 text-white">Reset Password</h2>
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-2 bg-white/10 border border-purple-400/50 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30"
          required
        />
      </div>

      {error && <p className="text-red-500 text-sm text-center">{error}</p>}
      {successMessage && <p className="text-green-500 text-sm text-center">{successMessage}</p>}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        {isLoading ? 'Sending...' : 'Send Reset Link'}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="w-full text-purple-400 hover:text-purple-300 text-sm mt-4"
      >
        Back to login
      </button>
    </form>
  </div>
);

const Login = ({ setIsAuthenticated }) => {
  const [showLogin, setShowLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  const getErrorMessage = useCallback((errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      default:
        return 'An error occurred. Please try again.';
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setIsAuthenticated(true);
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      setError(getErrorMessage(error.code));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      await sendEmailService(email);
      setSuccessMessage('Password reset email sent! Check your inbox.');
      setTimeout(() => {
        setShowLogin(true);
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(getErrorMessage(error.code));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-900 via-purple-900 to-black flex flex-col justify-center items-center px-4">
      <div className="flex flex-col items-center mb-8">
        <Link to="/">
          <img 
            src="/logo.png"
            alt="Dreamarium Logo"
            className="w-20 h-20 rounded-full bg-black border-1"
          />
        </Link>
        <span className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mt-4">
          Dreamarium
        </span>
      </div>
      <div className="bg-white/10 backdrop-blur-lg rounded-xl shadow-lg p-8 w-full max-w-md">
        {showLogin ? (
          <LoginForm
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            error={error}
            isLoading={isLoading}
            onSubmit={handleLogin}
            onForgotPassword={() => setShowLogin(false)}
            navigate={navigate}
          />
        ) : (
          <ForgotPasswordForm
            email={email}
            setEmail={setEmail}
            error={error}
            successMessage={successMessage}
            isLoading={isLoading}
            onSubmit={handlePasswordReset}
            onBack={() => {
              setShowLogin(true);
              setError('');
              setSuccessMessage('');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Login;